import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { CustomerService } from '../../../customer.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApplicationContextService } from '../../../../../shared/services/application-context.service';

/**
 * Feature Form Component
 * 
 * Purpose: Dynamically configure any feature for a customer
 * - Loads feature configuration from API
 * - Handles dynamic fields (regular and grouped)
 * - Supports multiple applications
 * - Saves configuration per application
 * 
 * Design Principles:
 * - No hard-coded feature IDs - everything is dynamic
 * - Clear separation of concerns
 * - Simple, readable logic
 * - Easy to extend for new features
 */

// Constants for better maintainability
const DEFAULT_STORAGE_APP_ID = 1;
const FIELD_TYPES = {
    TEXT: 'text',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    DATE: 'date',
    GROUP: 'group',
    SELECT: 'select'
} as const;

// Field type definitions for UI
const AVAILABLE_FIELD_TYPES = [
    { label: 'Text', value: FIELD_TYPES.TEXT },
    { label: 'Number', value: FIELD_TYPES.NUMBER },
    { label: 'Boolean', value: FIELD_TYPES.BOOLEAN },
    { label: 'Date', value: FIELD_TYPES.DATE },
    { label: 'Select', value: FIELD_TYPES.SELECT }
] as const;

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-feature-form',
    templateUrl: './feature-form.component.html',
    styleUrls: ['./feature-form.component.scss']
})
export class FeatureFormComponent implements OnInit, OnChanges {
    // Inputs: Data passed from parent component
    @Input() feature: any = null;
    @Input() customerId: string = '';

    // Outputs: Events emitted to parent component
    @Output() formSubmit = new EventEmitter<any>();
    @Output() formCancel = new EventEmitter<void>();

    // Form state
    featureForm!: FormGroup;
    isLoading: boolean = false;

    // Application management - now using global context
    currentApplicationId: number | null = null;


    // Dynamic fields management
    dynamicFieldsConfiguration: any[] = [];
    dynamicFieldsFormArray!: FormArray;
    customFieldsFormArray!: FormArray;

    // Expose constants to template
    readonly fieldTypes = AVAILABLE_FIELD_TYPES;

    constructor(
        private formBuilder: FormBuilder,
        private customerService: CustomerService,
        private commonService: CommonService,
        private applicationContextService: ApplicationContextService
    ) { }

    ngOnInit(): void {
        this.initializeForm();
        this.subscribeToApplicationContext();
        this.loadSavedConfiguration();
    }

    subscribeToApplicationContext(): void {
        this.applicationContextService.selectedApplicationId$.pipe(untilDestroyed(this)).subscribe(applicationId => {
            this.currentApplicationId = applicationId;
            if (applicationId && this.feature) {
                this.loadSavedConfiguration();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // When feature changes, reload everything
        if (changes['feature'] && !changes['feature'].firstChange && this.feature) {
            this.initializeForm();
            this.loadSavedConfiguration();
        }
    }

    /**
     * Initialize the main form structure
     * Creates form controls for: enabled status, dynamic fields.
     * Default enabled to false so that features not yet in customer-config show as disabled.
     */
    initializeForm(): void {
        const formControls: any = {
            enabled: [false, Validators.required]
        };

        // Initialize arrays for dynamic fields
        this.dynamicFieldsFormArray = this.formBuilder.array([]);
        formControls.dynamicFields = this.dynamicFieldsFormArray;

        this.customFieldsFormArray = this.formBuilder.array([]);
        formControls.customFields = this.customFieldsFormArray;

        this.featureForm = this.formBuilder.group(formControls);

        // Load dynamic fields from feature definition
        this.loadDynamicFieldsFromFeatureDefinition();
    }


    // Application loading methods removed - now using global application context


    /**
     * Load saved configuration for the current feature and application
     */
    private loadSavedConfiguration(): void {
        if (!this.customerId || !this.feature) return;

        const featureId = this.getFeatureId();
        const applicationId = this.getCurrentApplicationId();

        if (featureId && applicationId) {
            this.fetchAndApplySavedConfiguration(applicationId, featureId);
        }
    }

    /**
     * Get feature ID from feature object (handles different property names)
     */
    private getFeatureId(): number | null {
        if (!this.feature) return null;
        return this.feature.id || this.feature.feature_id || null;
    }

    /**
     * Get currently active application ID from global context
     */
    private getCurrentApplicationId(): number {
        return this.currentApplicationId || DEFAULT_STORAGE_APP_ID;
    }

    /**
     * Fetch saved configuration from API and apply to form
     */
    private fetchAndApplySavedConfiguration(applicationId: number, featureId: number): void {
        const requestParams = {
            id: this.customerId,
            feature_id: featureId,
            app_id: applicationId
        };

        this.customerService.getCustomerConfig(requestParams).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                if (response.status === 200 && response.data) {
                    // Handle different response structures
                    let configData = response.data;

                    // If data has a 'value' property, use that
                    if (configData && typeof configData === 'object' && configData.value !== undefined) {
                        configData = configData.value;
                    }

                    // If data has a 'feature_config' property, use that
                    if (configData && typeof configData === 'object' && configData.feature_config !== undefined) {
                        configData = configData.feature_config;
                    }

                    this.applyConfigurationToForm(configData, applicationId);
                }
            },
            error: (error: any) => {
                console.error('Error loading saved configuration:', error);
            }
        });
    }

    /**
     * Apply saved configuration data to form fields
     */
    private applyConfigurationToForm(configData: any, applicationId: number): void {
        console.log('applyConfigurationToForm: Received configData', configData, 'for applicationId', applicationId);

        // Check if configData is directly a feature_config array format
        // e.g., [{"due_date": {...}}, {"reminder_date": {...}}]
        if (Array.isArray(configData) && configData.length > 0) {
            const firstItem = configData[0];
            // Check if it's a feature_config array (each item has a single key-value pair)
            if (typeof firstItem === 'object' && firstItem !== null &&
                !firstItem.hasOwnProperty('application_id') &&
                !firstItem.hasOwnProperty('is_active') &&
                !firstItem.hasOwnProperty('value') &&
                !firstItem.hasOwnProperty('feature_config')) {
                // This is directly a feature_config array, convert and apply
                console.log('applyConfigurationToForm: Detected feature_config array format, converting...');
                const valueObject = this.convertFeatureConfigArrayToObject(configData);
                console.log('applyConfigurationToForm: Converted to object', valueObject);
                this.applyValuesToFormFields(valueObject);
                return;
            }
        }

        // Handle standard config format with application_id
        console.log('applyConfigurationToForm: Using standard config format');
        const configArray = Array.isArray(configData) ? configData : [configData];

        configArray.forEach((config: any) => {
            const configAppId = this.extractApplicationId(config.application_id);
            if (configAppId === applicationId) {
                console.log('applyConfigurationToForm: Applying config for application', applicationId);
                this.applySingleConfiguration(config);
            } else {
                console.log('applyConfigurationToForm: Skipping config for application', configAppId, '(expected', applicationId, ')');
            }
        });
    }

    /**
     * Extract application ID from various formats (array, number, string)
     */
    private extractApplicationId(applicationId: any): number | null {
        if (!applicationId) return null;
        if (Array.isArray(applicationId)) return applicationId[0];
        if (typeof applicationId === 'number') return applicationId;
        const parsed = parseInt(applicationId);
        return isNaN(parsed) ? null : parsed;
    }

    /**
     * Apply a single configuration object to the form
     */
    private applySingleConfiguration(config: any): void {
        // Update enabled status
        if (config.is_active !== undefined) {
            this.featureForm.patchValue({ enabled: config.is_active });
        }

        // Parse and apply value from API response
        if (config.value) {
            const parsedValue = this.parseConfigurationValue(config.value);
            if (parsedValue) {
                // Check if parsed value is a feature_config array format
                if (Array.isArray(parsedValue) && parsedValue.length > 0) {
                    const firstItem = parsedValue[0];
                    if (typeof firstItem === 'object' && firstItem !== null &&
                        Object.keys(firstItem).length === 1) {
                        // This is a feature_config array, convert to object
                        const valueObject = this.convertFeatureConfigArrayToObject(parsedValue);
                        this.applyValuesToFormFields(valueObject);
                    } else {
                        // Regular object, apply directly
                        this.applyValuesToFormFields(parsedValue);
                    }
                } else {
                    // Regular object, apply directly
                    this.applyValuesToFormFields(parsedValue);
                }
            }
        }

        // Fallback: use feature_config if value is not available
        if (config.feature_config && Array.isArray(config.feature_config)) {
            const valueObject = this.convertFeatureConfigArrayToObject(config.feature_config);
            this.applyValuesToFormFields(valueObject);
        }
    }

    /**
     * Parse configuration value (handles JSON strings)
     */
    private parseConfigurationValue(value: any): any {
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch (e) {
                console.error('Error parsing configuration value:', e);
                return null;
            }
        }
        return value;
    }

    /**
     * Convert feature_config array to object
     * Input: [{ "key1": "value1" }, { "key2": "value2" }]
     * Output: { "key1": "value1", "key2": "value2" }
     * Also handles nested objects: [{ "due_date": {"type": "minute", "unit": 5} }]
     * Output: { "due_date": {"type": "minute", "unit": 5} }
     */
    private convertFeatureConfigArrayToObject(featureConfigArray: any[]): any {
        const result: any = {};
        featureConfigArray.forEach((item: any) => {
            if (typeof item === 'object' && item !== null) {
                Object.keys(item).forEach(key => {
                    // Preserve nested objects as-is (e.g., {"type": "minute", "unit": 5})
                    result[key] = item[key];
                });
            }
        });
        return result;
    }


    /**
     * Load dynamic fields from feature definition (field_schema or dynamic_fields)
     */
    loadDynamicFieldsFromFeatureDefinition(): void {
        // Clear existing fields
        this.dynamicFieldsConfiguration = [];
        while (this.dynamicFieldsFormArray.length > 0) {
            this.dynamicFieldsFormArray.removeAt(0);
        }

        // Get field definition from feature
        const fieldDefinition = this.feature?.field_schema || this.feature?.dynamic_fields;
        if (!fieldDefinition || typeof fieldDefinition !== 'object') return;

        // Process each field in the definition
        Object.keys(fieldDefinition).forEach(fieldKey => {
            const defaultValue = fieldDefinition[fieldKey];
            this.createFieldFromDefinition(fieldKey, defaultValue);
        });
    }

    /**
     * Create a form field from feature definition
     * Handles both regular fields and grouped fields
     */
    private createFieldFromDefinition(fieldKey: string, defaultValue: any): void {
        // Check if this is a grouped field (object with sub-fields)
        if (this.isGroupedField(defaultValue)) {
            this.createGroupedField(fieldKey, defaultValue);
        } else {
            this.createRegularField(fieldKey, defaultValue);
        }
    }

    /**
     * Check if a value represents a grouped field
     */
    private isGroupedField(value: any): boolean {
        return typeof value === 'object' &&
            !Array.isArray(value) &&
            value !== null &&
            !(value instanceof Date);
    }

    /**
     * Create a grouped field with sub-fields
     */
    private createGroupedField(fieldKey: string, groupDefaultValue: any): void {
        const subFieldsConfig: any[] = [];

        // Extract sub-fields from the group
        Object.keys(groupDefaultValue).forEach(subFieldKey => {
            const subFieldValue = groupDefaultValue[subFieldKey];
            const subFieldType = this.inferFieldType(subFieldValue);

            subFieldsConfig.push({
                key: subFieldKey,
                type: subFieldType,
                value: subFieldValue,
                defaultValue: subFieldValue
            });
        });

        // Store grouped field configuration
        this.dynamicFieldsConfiguration.push({
            key: fieldKey,
            type: FIELD_TYPES.GROUP,
            value: groupDefaultValue,
            defaultValue: groupDefaultValue,
            subFields: subFieldsConfig
        });

        // Create form structure for grouped field
        const groupFormGroup = this.formBuilder.group({});
        const subFieldsFormArray = this.formBuilder.array([]);

        subFieldsConfig.forEach(subField => {
            subFieldsFormArray.push(this.formBuilder.control(subField.value || ''));
        });

        groupFormGroup.addControl('subFields', subFieldsFormArray);
        this.dynamicFieldsFormArray.push(groupFormGroup);
    }

    /**
     * Create a regular (non-grouped) field
     */
    private createRegularField(fieldKey: string, defaultValue: any): void {
        // Check if this is a select field (has options property)
        if (defaultValue && typeof defaultValue === 'object' && defaultValue.options && Array.isArray(defaultValue.options)) {
            // This is a select field with options
            const selectValue = defaultValue.value || defaultValue.options[0] || '';

            this.dynamicFieldsConfiguration.push({
                key: fieldKey,
                type: FIELD_TYPES.SELECT,
                value: selectValue,
                defaultValue: selectValue,
                options: defaultValue.options
            });

            this.dynamicFieldsFormArray.push(
                this.formBuilder.control(selectValue || '', Validators.required)
            );
        } else {
            // Regular field
            const fieldType = this.inferFieldType(defaultValue);

            // Store field configuration
            this.dynamicFieldsConfiguration.push({
                key: fieldKey,
                type: fieldType,
                value: defaultValue,
                defaultValue: defaultValue
            });

            // Create form control with appropriate validators
            const validators: any[] = [];
            if (fieldType === FIELD_TYPES.NUMBER) {
                validators.push(Validators.pattern(/^-?\d+(\.\d+)?$/));
            }

            this.dynamicFieldsFormArray.push(
                this.formBuilder.control(defaultValue || '', validators)
            );
        }
    }

    /**
     * Infer field type from default value
     */
    private inferFieldType(value: any): string {
        if (typeof value === 'number') return FIELD_TYPES.NUMBER;
        if (typeof value === 'boolean') return FIELD_TYPES.BOOLEAN;
        if (value instanceof Date) return FIELD_TYPES.DATE;
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return FIELD_TYPES.DATE;
        return FIELD_TYPES.TEXT;
    }

    /**
     * Apply saved values to form fields
     */
    private applyValuesToFormFields(values: any): void {
        if (!values || typeof values !== 'object') {
            console.warn('applyValuesToFormFields: Invalid values object', values);
            return;
        }

        console.log('applyValuesToFormFields: Applying values', values);
        console.log('applyValuesToFormFields: Available fields', this.dynamicFieldsConfiguration.map(f => f.key));

        this.dynamicFieldsConfiguration.forEach((fieldConfig, index) => {
            if (fieldConfig.type === FIELD_TYPES.GROUP) {
                this.applyGroupedFieldValue(fieldConfig, index, values);
            } else {
                this.applyRegularFieldValue(fieldConfig, index, values);
            }
        });
    }

    /**
     * Apply value to a grouped field
     */
    private applyGroupedFieldValue(fieldConfig: any, index: number, values: any): void {
        const groupValue = values[fieldConfig.key];
        console.log(`applyGroupedFieldValue: Field "${fieldConfig.key}", groupValue:`, groupValue);

        if (!groupValue || typeof groupValue !== 'object' || Array.isArray(groupValue)) {
            console.warn(`applyGroupedFieldValue: Invalid groupValue for "${fieldConfig.key}"`, groupValue);
            return;
        }

        const fieldGroup: any = this.dynamicFieldsFormArray.at(index);
        if (!fieldGroup) {
            console.warn(`applyGroupedFieldValue: No fieldGroup found at index ${index}`);
            return;
        }

        const subFieldsArray: any = fieldGroup.get('subFields');
        if (!subFieldsArray || !fieldConfig.subFields) {
            console.warn(`applyGroupedFieldValue: No subFieldsArray or subFields config for "${fieldConfig.key}"`);
            return;
        }

        fieldConfig.subFields.forEach((subFieldConfig: any, subIndex: any) => {
            const subValue = groupValue[subFieldConfig.key];
            console.log(`applyGroupedFieldValue: Sub-field "${subFieldConfig.key}" =`, subValue);
            if (subValue !== undefined && subValue !== null) {
                const subControl = subFieldsArray.at(subIndex);
                if (subControl) {
                    subControl.setValue(subValue);
                    console.log(`applyGroupedFieldValue: Set value for "${subFieldConfig.key}" to`, subValue);
                } else {
                    console.warn(`applyGroupedFieldValue: No subControl found at index ${subIndex}`);
                }
            }
        });
    }

    /**
     * Apply value to a regular field
     */
    private applyRegularFieldValue(fieldConfig: any, index: number, values: any): void {
        let value = values[fieldConfig.key];
        console.log(`applyRegularFieldValue: Field "${fieldConfig.key}", value:`, value);

        // Handle select field: value might be in an object with options
        if (fieldConfig.type === FIELD_TYPES.SELECT && value && typeof value === 'object' && value.value !== undefined) {
            value = value.value;
        }

        if (value === undefined || value === null) {
            console.warn(`applyRegularFieldValue: No value found for "${fieldConfig.key}"`);
            return;
        }

        const control = this.dynamicFieldsFormArray.at(index);
        if (!control) {
            console.warn(`applyRegularFieldValue: No control found at index ${index} for "${fieldConfig.key}"`);
            return;
        }

        const processedValue = this.processFieldValueByType(value, fieldConfig.type);
        control.setValue(processedValue);
        console.log(`applyRegularFieldValue: Set value for "${fieldConfig.key}" to`, processedValue);
    }
    private processFieldValueByType(value: any, fieldType: string): any {
        if (fieldType === FIELD_TYPES.NUMBER) {
            const numValue = parseFloat(value);
            return isNaN(numValue) ? value : numValue;
        }
        if (fieldType === FIELD_TYPES.BOOLEAN) {
            return value === 'true' || value === true || value === '1' || value === 1;
        }
        return value;
    }


    // Application selection methods removed - now using global application context

    /**
     * Load configuration for a specific application
     */
    private loadConfigurationForApplication(applicationId: number): void {
        const featureId = this.getFeatureId();
        if (!featureId || !this.customerId) return;

        // Reset form values before loading new ones
        this.resetFormFieldValues();
        // Fetch and apply configuration
        this.fetchAndApplySavedConfiguration(applicationId, featureId);
    }

    /**
     * Reset all form field values to defaults
     */
    private resetFormFieldValues(): void {
        this.dynamicFieldsFormArray.controls.forEach((control, index) => {
            const fieldConfig = this.dynamicFieldsConfiguration[index];
            if (!fieldConfig) return;

            if (fieldConfig.type === FIELD_TYPES.GROUP) {
                this.resetGroupedField(control, fieldConfig);
            } else {
                control.setValue(fieldConfig.defaultValue || '');
            }
        });
    }

    /**
     * Reset grouped field values
     */
    private resetGroupedField(control: any, fieldConfig: any): void {
        const fieldGroup: any = control;
        const subFieldsArray: any = fieldGroup.get('subFields');
        if (!subFieldsArray || !fieldConfig.subFields) return;

        fieldConfig.subFields.forEach((subFieldConfig: any, subIndex: any) => {
            const subControl = subFieldsArray.at(subIndex);
            if (subControl) {
                subControl.setValue(subFieldConfig.defaultValue || '');
            }
        });
    }

    // selectAllApplications method removed - now using global application context

    // ==================== Custom Fields Management ====================

    /**
     * Add a new custom field
     */
    addCustomField(): void {
        const newFieldGroup = this.formBuilder.group({
            key: ['', [Validators.required, this.validateUniqueKey.bind(this)]],
            value: ['', [Validators.required]],
            type: [FIELD_TYPES.TEXT, [Validators.required]]
        });
        this.customFieldsFormArray.push(newFieldGroup);
    }

    /**
     * Remove a custom field
     */
    removeCustomField(index: number): void {
        if (this.customFieldsFormArray.length > index) {
            this.customFieldsFormArray.removeAt(index);
        }
    }

    /**
     * Validate that field key is unique
     */
    private validateUniqueKey(control: FormControl): { [key: string]: any } | null {
        if (!control.value) return null;

        const key = control.value.trim().toLowerCase();
        const allKeys = [
            ...this.dynamicFieldsConfiguration.map(f => f.key?.toLowerCase()),
            ...Array.from({ length: this.customFieldsFormArray.length }, (_, i) => {
                const fieldGroup = this.customFieldsFormArray.at(i);
                const fieldKey = fieldGroup?.get('key')?.value;
                return fieldKey !== control.value ? fieldKey?.toLowerCase() : null;
            }).filter(k => k)
        ];

        return allKeys.includes(key) ? { duplicateKey: true } : null;
    }

    /**
     * Get all dynamic fields (predefined + custom)
     */
    private getAllDynamicFields(): any[] {
        const predefined = this.dynamicFieldsConfiguration.map(f => ({
            key: f.key,
            type: f.type,
            value: f.value ?? f.defaultValue ?? '',
            isCustom: false
        }));

        const custom = Array.from({ length: this.customFieldsFormArray.length }, (_, i) => {
            const fieldGroup = this.customFieldsFormArray.at(i);
            return {
                key: fieldGroup?.get('key')?.value || '',
                type: fieldGroup?.get('type')?.value || FIELD_TYPES.TEXT,
                value: fieldGroup?.get('value')?.value ?? '',
                isCustom: true
            };
        }).filter(f => f.key);

        return [...predefined, ...custom];
    }

    // ==================== Form Submission ====================

    /**
     * Handle form submission
     */
    onSubmit(): void {
        if (!this.featureForm.valid || !this.feature) {
            this.markFormAsTouched();
            return;
        }

        this.isLoading = true;
        const featureConfig = this.buildFeatureConfigFromForm();
        const payload = this.buildFeaturePayload(featureConfig);

        this.saveConfiguration(payload, () => {
            this.commonService.message.success('Configuration saved successfully');
        });
    }

    /**
     * Build feature config from form values
     */
    private buildFeatureConfigFromForm(): any[] {
        const dynamicFieldsObject = this.collectDynamicFieldsFromForm();

        // Convert dynamic fields object to array format
        const featureConfigArray: any[] = [];
        Object.keys(dynamicFieldsObject).forEach(key => {
            featureConfigArray.push({ [key]: dynamicFieldsObject[key] });
        });

        return featureConfigArray;
    }

    /**
     * Collect all dynamic field values from form
     */
    private collectDynamicFieldsFromForm(): { [key: string]: any } {
        const result: { [key: string]: any } = {};
        const fieldDefinition = this.feature?.field_schema || this.feature?.dynamic_fields || {};

        // Process predefined fields
        Object.keys(fieldDefinition).forEach(fieldKey => {
            const fieldConfig = this.dynamicFieldsConfiguration.find(f => f.key === fieldKey);
            if (!fieldConfig) return;

            const fieldIndex = this.dynamicFieldsConfiguration.indexOf(fieldConfig);
            const defaultValue = fieldDefinition[fieldKey];

            if (fieldConfig.type === FIELD_TYPES.GROUP) {
                const groupValue = this.collectGroupedFieldValue(fieldConfig, fieldIndex);
                if (groupValue && Object.keys(groupValue).length > 0) {
                    result[fieldKey] = groupValue;
                } else if (this.isGroupedField(defaultValue)) {
                    result[fieldKey] = defaultValue; // Use default if no values entered
                }
            } else if (fieldConfig.type === FIELD_TYPES.SELECT) {
                // For select fields, just store the selected value (not the options object)
                const fieldValue = this.collectRegularFieldValue(fieldIndex, defaultValue);
                if (fieldValue !== undefined && fieldValue !== null) {
                    result[fieldKey] = fieldValue;
                }
            } else if (fieldConfig.type === FIELD_TYPES.SELECT) {
                // For select fields, just store the selected value (not the options object)
                const fieldValue = this.collectRegularFieldValue(fieldIndex, defaultValue);
                if (fieldValue !== undefined && fieldValue !== null) {
                    result[fieldKey] = fieldValue;
                }
            } else {
                const fieldValue = this.collectRegularFieldValue(fieldIndex, defaultValue);
                if (fieldValue !== undefined && fieldValue !== null) {
                    result[fieldKey] = fieldValue;
                }
            }
        });

        // Process custom fields
        this.getAllDynamicFields().forEach(field => {
            if (field.isCustom && field.key && !result[field.key]) {
                const customFieldIndex = this.customFieldsFormArray.controls.findIndex(
                    (ctrl) => ctrl.get('key')?.value === field.key
                );
                if (customFieldIndex !== -1) {
                    const value = this.customFieldsFormArray.at(customFieldIndex)?.get('value')?.value;
                    if (value !== null && value !== undefined && value !== '') {
                        result[field.key] = this.processFieldValueByType(value, field.type);
                    }
                }
            }
        });

        return result;
    }

    /**
     * Collect value from a grouped field
     */
    private collectGroupedFieldValue(fieldConfig: any, fieldIndex: number): any {
        const fieldGroup: any = this.dynamicFieldsFormArray.at(fieldIndex);
        if (!fieldGroup) return null;

        const subFieldsArray: any = fieldGroup.get('subFields');
        if (!subFieldsArray || !fieldConfig.subFields) return null;

        const groupValue: any = {};
        fieldConfig.subFields.forEach((subFieldConfig: any, subIndex: any) => {
            let subValue = subFieldsArray.at(subIndex)?.value;

            // Use default if empty
            if (this.isEmptyValue(subValue)) {
                subValue = subFieldConfig.defaultValue;
            }

            // Process by type
            const processedValue = this.processFieldValueByType(subValue, subFieldConfig.type);
            if (processedValue !== undefined && processedValue !== null) {
                groupValue[subFieldConfig.key] = processedValue;
            }
        });

        return groupValue;
    }

    /**
     * Collect value from a regular field
     */
    private collectRegularFieldValue(fieldIndex: number, defaultValue: any): any {
        let value = this.dynamicFieldsFormArray.at(fieldIndex)?.value;

        // Use default if empty
        if (this.isEmptyValue(value)) {
            value = defaultValue;
        }

        // Process by type
        const fieldConfig = this.dynamicFieldsConfiguration[fieldIndex];
        if (fieldConfig) {
            return this.processFieldValueByType(value, fieldConfig.type);
        }

        return value;
    }

    /**
     * Check if a value is considered empty
     */
    private isEmptyValue(value: any): boolean {
        return value === null ||
            value === undefined ||
            value === '' ||
            (typeof value === 'string' && value.trim() === '');
    }


    /**
     * Build payload for feature submission
     */
    private buildFeaturePayload(featureConfig: any[]): any {
        const userId = localStorage.getItem('user_id') || '';
        const featureId = this.getFeatureId();
        const selectedAppIds = this.currentApplicationId ? [this.currentApplicationId] : [];
        return {
            customer_id: this.customerId,
            feature_id: featureId,
            created_by: Number(userId),
            is_active: !!this.featureForm.get('enabled')?.value,
            application_id: selectedAppIds,
            feature_config: featureConfig.length > 0 ? featureConfig : undefined
        };
    }


    private saveConfiguration(payload: any, onSuccess?: () => void): void {
        this.customerService.addCustomerConfig(payload).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                if (response.status !== 200) {
                    this.commonService.message.error(response.message || 'Failed to save configuration');
                } else {
                    if (onSuccess) onSuccess();
                    this.formSubmit.emit({ ...payload, ...this.featureForm.value, apiResponse: response });
                }
                this.isLoading = false;
            },
            error: (error: any) => {
                console.error('Error saving configuration:', error);
                this.commonService.message.error('An error occurred while saving configuration');
                this.isLoading = false;
            }
        });
    }

    /**
     * Mark all form controls as touched (for validation display)
     */
    private markFormAsTouched(): void {
        Object.values(this.featureForm.controls).forEach(control => {
            control.markAsDirty();
            control.updateValueAndValidity();
        });
    }

    /**
     * Handle cancel action
     */
    onCancel(): void {
        this.formCancel.emit();
    }

}
